"use client";
import { useState, useRef, useMemo, useDeferredValue } from "react";

import Icon from "@/components/icons/Icons";

import CustomDropdown from "@/components/ui/CustomDropdown";
import { TableRollingReturns } from "@/type/rollingPageTypes";
import { formatDate } from "@/utils/commonFunctions";
import { Pagination } from "@/components/navigation/Pagination";

interface RollingReturnsTableProps {
  tableData: TableRollingReturns[];
  showTable: boolean;
}

interface TableRow {
  date: string;
  values: (number | "-")[];
}

export default function RollingNavTable({ tableData, showTable }: RollingReturnsTableProps) {
  /* -------------------- Build Merged Rows (Memoized) -------------------- */
  const rows: TableRow[] = useMemo(() => {
    const firstFund = tableData[0];
    if (!firstFund?.data?.returns?.length) return [];

    return firstFund.data.returns.map((item, rowIndex) => {
      const date = ` <span>${formatDate(item.startDate, "dd-MM-yyyy")}</span>
                      <span>to</span>
                      <span>${formatDate(item.endDate, "dd-MM-yyyy")}</span>
                    `;

      return {
        date,
        values: tableData.map((fund) => fund.data.returns[rowIndex]?.returnPercentage ?? "-"),
      };
    });
  }, [tableData]);

  /* -------------------- State -------------------- */
  const [search, setSearch] = useState<string>("");
  const deferredSearch = useDeferredValue(search); // smoother typing

  const [entries, setEntries] = useState<number>(10);
  const [isEntriesOpen, setIsEntriesOpen] = useState<boolean>(false);
  const entriesBtnRef = useRef<HTMLButtonElement>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  /* -------------------- Search (Memoized) -------------------- */
  const filteredData = useMemo(() => {
    if (!deferredSearch.trim()) return rows;

    const lower = deferredSearch.toLowerCase();
    return rows.filter((row) => row.date.toLowerCase().includes(lower));
  }, [rows, deferredSearch]);

  /* -------------------- Pagination (Memoized) -------------------- */
  const { paginatedData, totalPages, startIndex, endIndex } = useMemo(() => {
    const rowPerPage = entries;
    const start = (currentPage - 1) * rowPerPage;
    const end = start + rowPerPage;

    return {
      paginatedData: filteredData.slice(start, end),
      totalPages: Math.ceil(filteredData.length / rowPerPage),
      startIndex: start,
      endIndex: end,
    };
  }, [filteredData, entries, currentPage]);

  const minWidthColumns = tableData.length == 5 ? "min-w-[1080px]" : tableData.length == 4 ? "min-w-[916px]" : tableData.length == 3 ? "min-w-[712px]" : tableData.length == 2 ? "min-w-[504px]" : "min-w-76";
  const maxWidthColumns = tableData.length == 5 ? "200px" : tableData.length == 4 ? "242px" : tableData.length == 3 ? "322px" : tableData.length == 2 ? "483px" : "966px";

  if (!showTable || !tableData.length) return;

  return (
    <div className={`w-full md:max-w-298 lg:rounded-6xl mx-auto py-4 sm:p-4 sm:border bg-white`}>
      <div className="flex flex-col items-start xs:flex-row xs:items-center xs:justify-between mb-4 mt-2 gap-3 px-4 sm:px-0">
        <div className="flex items-center gap-2">
          <span>Show</span>

          <div className="relative">
            <button ref={entriesBtnRef} onClick={() => setIsEntriesOpen((p) => !p)} className="border bg-white px-3 py-1 rounded-md flex justify-between items-center min-w-22.5">
              <span className="text-left">{entries}</span>
              <Icon.dropdownDownArrow className={`w-3 h-2 transition-transform duration-300 ${isEntriesOpen ? "rotate-180" : "rotate-0"}`} />
            </button>

            <CustomDropdown isOpen={isEntriesOpen} onClose={() => setIsEntriesOpen(false)} excludeRefs={[entriesBtnRef]} className="mt-1 rounded-lg shadow-md w-full z-2 p-1.5 space-y-1 overflow-hidden" useHeight>
              {[10, 20, 50, 100].map((value) => (
                <button
                  key={value}
                  onClick={() => {
                    setEntries(value);
                    setCurrentPage(1);
                    setIsEntriesOpen(false);
                  }}
                  className={`w-full px-3.5 py-1 text-sm justify-center rounded-xs text-center ${entries === value ? "bg-gray-100 font-semibold" : "hover:bg-gray-100"}`}
                >
                  {value}
                </button>
              ))}
            </CustomDropdown>
          </div>

          <span>Entries</span>
        </div>

        <label className="group max-w-52 w-full sm:ml-auto py-1 px-1.5 rounded-md text-left bg-white border border-[#949494] text-sm font-medium flex items-center justify-between gap-1 hover:border-[#5a5a5a] text-black/60 focus-within:text-black/90 focus-within:border-[#5a5a5a] transition duration-300">
          <Icon.searchIcon2 className="w-4 h-4 shrink-0 text-[#949494] transition duration-300 group-hover:text-[#5a5a5a]" />

          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full bg-transparent text-[#949494] placeholder:text-black/60"
            placeholder="Search Date"
          />

          {search && (
            <button onClick={() => setSearch("")} className="ml-auto shrink-0">
              <Icon.crossIcon className="w-3 h-3 hover:text-primaryBlue" />
            </button>
          )}
        </label>
      </div>

      <div className="w-full overflow-x-auto scrollbar-xs mb-4 sm:mb-5">
        <table className={`border-collapse border border-[#d2d5da] text-center ${minWidthColumns}`}>
          <thead>
            <tr className="border-b border-[#d2d5da]">
              <th className="w-28 xxs:w-48 py-3 px-5 text-sm border-r font-medium border-[#d2d5da]">
                <div className="max-w-28 xxs:max-w-48">Dates</div>
              </th>

              {tableData.map((fund, index) => (
                <th key={index} className="truncate p-3 text-sm font-medium border-r border-[#d2d5da] last:border-r-0" style={{ width: maxWidthColumns, maxWidth: maxWidthColumns }}>
                  {fund.name}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {paginatedData.map((row, rowIndex) => (
              <tr key={`${row.date}-${rowIndex}`} className="border-b border-[#d2d5da] odd:bg-primaryBlack/5 last:border-b-0">
                <td className="w-28 xxs:w-48 py-3 px-5 text-2xs border-r border-[#d2d5da] whitespace-nowrap">
                  <div className="flex flex-col xxs:flex-row items-center xs:gap-2" dangerouslySetInnerHTML={{ __html: row.date }} />
                </td>

                {row.values.map((value, colIndex) => {
                  if (typeof value !== "number") {
                    return (
                      <td key={colIndex} className="py-3 px-2 text-2xs border-r border-[#d2d5da] last:border-r-0">
                        -
                      </td>
                    );
                  }

                  return (
                    <td key={colIndex} className="py-3 px-2 text-2xs border-r border-[#d2d5da] last:border-r-0">
                      <span className={value > 0 ? "text-green-600" : value < 0 ? "text-red-600" : ""}>{value.toFixed(2)}%</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col xs:flex-row items-center gap-3 px-4 sm:px-0">
        <div className="text-center sm::text-left">
          Showing {filteredData.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} entries
        </div>

        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={(v) => setCurrentPage(v)} className="sm:ml-auto" />
      </div>
    </div>
  );
}
